import { useCallback } from "react";
import { useRouter } from "next/router";

import { GenericObject } from "@vahak/core/dist/custom-types";
import { useCsDashboard } from "@vahak/core/dist/hooks/useCsDashboard";

import { POSTING_FORM_MODAL_ORIGINATION_PAGE, useLoadLorryPosting } from "../context/load-lorry-posting";
import { LOAD_CREATION_FORM_IDS, loadCreationFormFieldsTypes } from "../components/posting/helpers/constants";
import { LOAD_POSTING_FORM_LOCATION_STATE } from "../components/posting/load-posting/helpers/constants";
import { LORRY_POSTING_FORM_LOCATION_STATE } from "../components/posting/lorry-posting/helpers/constants";
import { useCompactLoadPostingOptional } from "../components/homepage-v2/compact-load-posting/CompactLoadPostingProvider";
import { LoadQuoteValues } from "../components/homepage-v2/load-quote";
import { mapLoadFormValuesToCompactInitialValues } from "../components/homepage-v2/compact-load-posting/mapLoadFormValuesToCompactInitialValues";
import useValidateUserStatus from "./useValidateUserStatus";

export interface OpenNewLoadPostingArgs {
    originationPage: POSTING_FORM_MODAL_ORIGINATION_PAGE;
    category?: "post" | "re-post";
    /** Prefilled route/lorry values for the compact flow (consumer users only). */
    initialValues?: LoadQuoteValues;
    /** Legacy load form values for repost prefill (consumer dashboard repost). */
    prefilledLoadData?: loadCreationFormFieldsTypes;
    /** Original load id when reposting (analytics / session key only). */
    repostLoadId?: number;
    /** Marks the first post for the legacy flow (CS dashboard users). */
    firstPost?: boolean;
    /** Starting step for the legacy modal (CS dashboard users). Defaults to load location details. */
    loadStep?: string;
    /** Extra analytics payload merged into the compact flow session. */
    eventData?: GenericObject;
    /** Runs after the relevant modal has been triggered. */
    afterOpen?: () => void;
    /** Called after a successful compact post / repost. */
    onSuccess?: () => void;
    /** Skips the login/validation gate (e.g. auto-open flows that already validated). */
    skipValidation?: boolean;
}

/**
 * Single entry point for opening a fresh ("post") or "re-post" load posting flow.
 *
 * Consumer users get the compact flow. CS dashboard users keep the legacy standalone modal.
 */
export const useOpenNewLoadPosting = () => {
    const router = useRouter();
    const { isCsDashboardUser } = useCsDashboard();
    const openCompactPosting = useCompactLoadPostingOptional()?.openCompactPosting;
    const { updateLoadLorryPostingData, openLoadPostingModal } = useLoadLorryPosting();
    const { ValidateLoadPosting } = useValidateUserStatus();

    return useCallback(
        ({
            originationPage,
            category = "post",
            initialValues,
            prefilledLoadData,
            repostLoadId,
            firstPost,
            loadStep = LOAD_CREATION_FORM_IDS.LOAD_LOCATION_DETAILS,
            eventData,
            afterOpen,
            onSuccess,
            skipValidation
        }: OpenNewLoadPostingArgs) => {
            if (!isCsDashboardUser && openCompactPosting) {
                const compactValues = prefilledLoadData
                    ? mapLoadFormValuesToCompactInitialValues(prefilledLoadData, repostLoadId)
                    : initialValues;

                openCompactPosting(compactValues, {
                    ...(eventData ? { eventData } : {}),
                    isRepost: category === "re-post",
                    onSuccess
                });
                afterOpen?.();
                return;
            }

            const openLegacy = () => {
                updateLoadLorryPostingData?.({
                    isModalOpen: true,
                    type: "load",
                    category,
                    originationPage,
                    firstPost,
                    ...(prefilledLoadData &&
                        repostLoadId && {
                            data: {
                                id: repostLoadId,
                                values: prefilledLoadData
                            }
                        }),
                    onSuccess
                });
                delete router.query[LORRY_POSTING_FORM_LOCATION_STATE.PARAM_NAME];
                router.query[LOAD_POSTING_FORM_LOCATION_STATE.PARAM_NAME] = loadStep;
                openLoadPostingModal?.(loadStep);
                afterOpen?.();
            };

            if (skipValidation) {
                openLegacy();
                return;
            }

            ValidateLoadPosting(openLegacy);
        },
        [
            isCsDashboardUser,
            openCompactPosting,
            updateLoadLorryPostingData,
            openLoadPostingModal,
            router,
            ValidateLoadPosting
        ]
    );
};

export default useOpenNewLoadPosting;
